import React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { buildSection30Summary } from "../utils/section30Summary";

const QuestionBlock = ({ number, label, description, children }) => (
  <Box sx={{ mb: 0 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
      {number}. {label}
    </Typography>
    {description && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.25, fontStyle: "italic", fontSize: "0.82rem", lineHeight: 1.5 }}
      >
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

const OptionDescription = ({ children }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ mt: 0.5, mb: 0.5, fontSize: "0.81rem", lineHeight: 1.5, pl: 0.5 }}
  >
    {children}
  </Typography>
);

function Section30Form({ index, data, setData }) {
  const exp = data[index];
  const section30 = exp.section30 || {};
  const summary = buildSection30Summary(exp, index);

  const update = (field, value) => {
    const newData = [...data];
    newData[index] = {
      ...newData[index],
      section30: {
        ...(newData[index].section30 || {}),
        [field]: value,
      },
    };
    setData(newData);
  };

  // Auto-calculate SES for Cohen's d
  const effectSizeNum = parseFloat(section30.ssEffectSize);
  const sdNum = parseFloat(section30.ssSd);
  const autoSES =
    !isNaN(effectSizeNum) && !isNaN(sdNum) && sdNum !== 0
      ? (effectSizeNum / sdNum).toFixed(3)
      : "";

  return (
    <Box
      sx={{ mt: 4, p: 3, border: "1px solid #ccc", borderRadius: 2, backgroundColor: "#fafafa" }}
    >
      <Box sx={{ mb: 2, px: 2, py: 1, borderLeft: "4px solid", borderColor: "primary.main", backgroundColor: "primary.50" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
          Section 30 — Justification of Animal Numbers
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Answer the questions below for this experiment. The app will automatically generate a
        numbered summary paragraph to be included in the document preview and export.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        These questions apply to the experiment configured in Section 29. Complete one Section 30
        per experiment. The generated summary at the bottom mirrors the output expected by Form A.
      </Alert>

      {/* ── Q1 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock number="1" label="What kind of experiment is this?">
        <FormControl fullWidth size="small">
          <InputLabel id={`experiment-type-${index}`}>Type of experiment</InputLabel>
          <Select
            labelId={`experiment-type-${index}`}
            value={section30.experimentType || ""}
            label="Type of experiment"
            onChange={(e) => update("experimentType", e.target.value)}
          >
            <MenuItem value="pilot">Pilot experiment</MenuItem>
            <MenuItem value="exploratory">Exploratory experiment</MenuItem>
            <MenuItem value="confirmatory">Confirmatory experiment</MenuItem>
          </Select>
        </FormControl>
        {section30.experimentType === "pilot" && (
          <OptionDescription>
            A pilot experiment involves few animals to assess feasibility, identify potential
            problems, optimize protocols, estimate variability, and reduce ethical and resource
            risks before a full study.
          </OptionDescription>
        )}
        {section30.experimentType === "exploratory" && (
          <OptionDescription>
            An exploratory experiment does not contain a pre-established formal hypothesis. It aims
            at measuring many parameters which will then allow a hypothesis to be formulated and
            tested formally in a confirmatory experiment.
          </OptionDescription>
        )}
        {section30.experimentType === "confirmatory" && (
          <OptionDescription>
            A confirmatory experiment aims at confirming a hypothesis based on prior exploratory
            results, evaluating the effectiveness or safety of treatments, or generalizing results
            to larger populations. It requires appropriate control groups, adequate randomization,
            and standardized experimental procedures.
          </OptionDescription>
        )}
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q2 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="2"
        label="What is the experimental unit?"
        description="The experimental unit is the smallest division of the experimental material such that any two units can receive different treatments."
      >
        <FormControl component="fieldset">
          <RadioGroup
            value={section30.experimentalUnit || ""}
            onChange={(e) => update("experimentalUnit", e.target.value)}
          >
            <FormControlLabel
              value="animal"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    The animal
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.81rem" }}>
                    Each animal may receive a different treatment (even those housed in the same
                    cage). Measures are taken on individual animals and statistics compare
                    individual animals.
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: "flex-start" }}
            />
            <FormControlLabel
              value="cage"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    The cage
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.81rem" }}>
                    All animals in the same cage receive the same treatment. Measures are taken per
                    cage or averaged per cage (e.g. body weight, water consumption). Statistics
                    compare cages.
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: "flex-start" }}
            />
            <FormControlLabel
              value="other"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" sx={{ fontWeight: "500" }}>
                  Other
                </Typography>
              }
              sx={{ alignItems: "flex-start" }}
            />
          </RadioGroup>
        </FormControl>
        {section30.experimentalUnit === "other" && (
          <TextField
            fullWidth
            size="small"
            label="Describe the experimental unit"
            value={section30.experimentalUnitOther || ""}
            onChange={(e) => update("experimentalUnitOther", e.target.value)}
            sx={{ mt: 1 }}
          />
        )}
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q3 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="3"
        label="What is the main factor you want to study?"
        description="This is the variable you want to measure and compare between groups (e.g. treatment X vs Y, knock-out vs wild type, dose A vs dose B). If two or more factors are equally important, use a factorial design. The main factor should be the one with the highest variability, as it is the parameter that justifies the required number of animals."
      >
        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <RadioGroup
            value={section30.mainFactorType || "single"}
            onChange={(e) => update("mainFactorType", e.target.value)}
          >
            <FormControlLabel
              value="single"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Single main factor</Typography>}
            />
            <FormControlLabel
              value="factorial"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Factorial design — two or more equally important factors
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>
        {(!section30.mainFactorType || section30.mainFactorType === "single") && (
          <TextField
            fullWidth
            size="small"
            label="The main factor is"
            placeholder="e.g. treatment X vs treatment Y, knock-out vs wild type"
            value={section30.mainFactor || ""}
            onChange={(e) => update("mainFactor", e.target.value)}
          />
        )}
        {section30.mainFactorType === "factorial" && (
          <TextField
            fullWidth
            size="small"
            label="The factors to be studied are"
            placeholder="e.g. treatment and sex, strain and age"
            value={section30.factorialFactors || ""}
            onChange={(e) => update("factorialFactors", e.target.value)}
          />
        )}
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q4 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="4"
        label="What is the outcome you want to measure?"
        description="This is the parameter you will use to study the main factor(s). For example, if the factor is 'treatment', which effect will you measure? e.g. blood pressure, glucose concentration, behavioural response."
      >
        <TextField
          fullWidth
          size="small"
          label="Outcome to measure"
          placeholder="e.g. blood pressure, glucose concentration, behavioural response"
          value={section30.outcomeMeasure || ""}
          onChange={(e) => update("outcomeMeasure", e.target.value)}
        />
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q5 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="5"
        label="What is your hypothesis about this outcome?"
        description="How do you expect this outcome to vary between groups if your hypothesis is true? If the experiment involves multiple time-points, include them in your formulation. Consider stating the effect size: the minimum difference between conditions that you consider biologically significant (e.g. glucose concentration in treated animals will be at least 30% lower than in untreated animals)."
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="The hypothesis is"
          placeholder="Describe how you expect the outcome to vary between groups..."
          value={section30.hypothesis || ""}
          onChange={(e) => update("hypothesis", e.target.value)}
        />
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q6 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="6"
        label="What variability do you expect for this outcome?"
        description="Express as the standard deviation, based on previous experiments or published literature."
      >
        <FormControl component="fieldset">
          <RadioGroup
            value={section30.standardDeviationSource || ""}
            onChange={(e) => update("standardDeviationSource", e.target.value)}
          >
            <FormControlLabel
              value="known"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Known or estimated — I can provide a standard deviation value
                </Typography>
              }
            />
            <FormControlLabel
              value="unknown_first_time"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Unknown — I cannot estimate it because this is the first time I perform this
                  experiment
                </Typography>
              }
            />
            <FormControlLabel
              value="unknown_pilot"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Unknown — I will estimate it in a pilot experiment
                </Typography>
              }
            />
            <FormControlLabel
              value="other"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Other</Typography>}
            />
          </RadioGroup>
        </FormControl>
        {section30.standardDeviationSource === "known" && (
          <TextField
            fullWidth
            size="small"
            label="Standard deviation value"
            placeholder="e.g. 2.3 mmHg"
            value={section30.standardDeviationValue || ""}
            onChange={(e) => update("standardDeviationValue", e.target.value)}
            helperText="Include units and the source or reference if applicable."
            sx={{ mt: 1 }}
          />
        )}
        {section30.standardDeviationSource === "other" && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Explain"
            value={section30.standardDeviationNotes || ""}
            onChange={(e) => update("standardDeviationNotes", e.target.value)}
            sx={{ mt: 1 }}
          />
        )}
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q7 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock number="7" label="Which experimental design will you use?">
        <FormControl fullWidth size="small">
          <InputLabel id={`design-type-${index}`}>Experimental design</InputLabel>
          <Select
            labelId={`design-type-${index}`}
            value={section30.experimentalDesignType || ""}
            label="Experimental design"
            onChange={(e) => update("experimentalDesignType", e.target.value)}
          >
            <MenuItem value="completely_randomised_single_factor">
              Completely randomised single factor design
            </MenuItem>
            <MenuItem value="randomised_block">Randomised block design</MenuItem>
            <MenuItem value="completely_randomised_factorial">
              Completely randomised factorial design
            </MenuItem>
            <MenuItem value="randomised_block_factorial">
              Randomised block factorial design
            </MenuItem>
            <MenuItem value="split_plot">Split plot design</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        {section30.experimentalDesignType === "completely_randomised_single_factor" && (
          <OptionDescription>
            Treatments are allocated to experimental units at random. Best suited when variability
            between units is very low.
          </OptionDescription>
        )}
        {section30.experimentalDesignType === "randomised_block" && (
          <OptionDescription>
            The experiment is split into several blocks (mini-experiments). Each block contains one
            experimental unit per treatment. Useful when differences are expected between blocks
            (e.g. time of day, experimenter, environment). What is sometimes called a "repetition"
            is in fact a block in this design.
          </OptionDescription>
        )}
        {section30.experimentalDesignType === "completely_randomised_factorial" && (
          <OptionDescription>
            A completely randomised design with more than one factor (e.g. treatment and sex).
          </OptionDescription>
        )}
        {section30.experimentalDesignType === "randomised_block_factorial" && (
          <OptionDescription>
            A randomised block design with more than one factor (e.g. strain and age).
          </OptionDescription>
        )}
        {section30.experimentalDesignType === "split_plot" && (
          <OptionDescription>
            A randomised block design where one main factor changes between blocks (e.g. sex).
          </OptionDescription>
        )}
        {section30.experimentalDesignType === "other" && (
          <TextField
            fullWidth
            size="small"
            label="Describe the experimental design"
            value={section30.experimentalDesignOther || ""}
            onChange={(e) => update("experimentalDesignOther", e.target.value)}
            sx={{ mt: 1 }}
          />
        )}
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q8 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="8"
        label="What measures will you take to avoid biases?"
        description="Select all that apply."
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!section30.biasRandomisation}
                onChange={(e) => update("biasRandomisation", e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">Randomisation of animals between groups</Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!section30.biasBlinding}
                onChange={(e) => update("biasBlinding", e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Keep experimenters blind about group identity during procedures, measures, or data
                analysis
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!section30.biasOrderRandomised}
                onChange={(e) => update("biasOrderRandomised", e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Randomised order of treatment or measures between groups (e.g. avoid always
                treating controls first)
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!section30.biasInclusionExclusion}
                onChange={(e) => update("biasInclusionExclusion", e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Set inclusion and exclusion criteria in advance
              </Typography>
            }
          />
          {section30.biasInclusionExclusion && (
            <TextField
              fullWidth
              size="small"
              label="Describe the inclusion / exclusion criteria"
              placeholder="e.g. include only males aged 8–12 weeks; exclude animals with body weight below 20 g"
              value={section30.biasInclusionExclusionText || ""}
              onChange={(e) => update("biasInclusionExclusionText", e.target.value)}
              sx={{ ml: 4, mb: 1, width: "calc(100% - 32px)" }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!section30.biasOther}
                onChange={(e) => update("biasOther", e.target.checked)}
              />
            }
            label={<Typography variant="body2">Other</Typography>}
          />
          {section30.biasOther && (
            <TextField
              fullWidth
              size="small"
              label="Describe other bias-reduction measure"
              value={section30.biasOtherText || ""}
              onChange={(e) => update("biasOtherText", e.target.value)}
              sx={{ ml: 4, mb: 1, width: "calc(100% - 32px)" }}
            />
          )}
        </FormGroup>
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q9 ─────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="9"
        label="How did you calculate the sample size?"
        description="Select the method you used. You can use Gpower software to perform an a-priori power analysis."
      >
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id={`sample-size-method-${index}`}>Sample size method</InputLabel>
          <Select
            labelId={`sample-size-method-${index}`}
            value={section30.sampleSizeMethod || ""}
            label="Sample size method"
            onChange={(e) => update("sampleSizeMethod", e.target.value)}
          >
            <MenuItem value="apriori_power_analysis">A-priori power analysis</MenuItem>
            <MenuItem value="cohens_d">Cohen&apos;s d method (standardised effect size)</MenuItem>
            <MenuItem value="resource_equation">Resource equation method</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        {section30.sampleSizeMethod === "apriori_power_analysis" && (
          <Box sx={{ pl: 1.5, borderLeft: "3px solid #e0e0e0" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, fontSize: "0.82rem" }}
            >
              Enter the parameters used for the power analysis. All animal counts refer to numbers
              per group.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Effect size"
                  placeholder="e.g. 1.5"
                  value={section30.ssEffectSize || ""}
                  onChange={(e) => update("ssEffectSize", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Standard deviation"
                  placeholder="e.g. 2.3"
                  value={section30.ssSd || ""}
                  onChange={(e) => update("ssSd", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Significance level (α, type I error)"
                  placeholder="e.g. 0.05"
                  value={section30.ssAlpha || ""}
                  onChange={(e) => update("ssAlpha", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Power (1 − β, type II error)"
                  placeholder="e.g. 0.80"
                  value={section30.ssPower || ""}
                  onChange={(e) => update("ssPower", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alternative hypothesis"
                  placeholder="e.g. two-sided, one-sided greater"
                  value={section30.ssAlternativeHypothesis || ""}
                  onChange={(e) => update("ssAlternativeHypothesis", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>One-tailed or two-tailed test</InputLabel>
                  <Select
                    value={section30.ssTailed || ""}
                    label="One-tailed or two-tailed test"
                    onChange={(e) => update("ssTailed", e.target.value)}
                  >
                    <MenuItem value="one-tailed">One-tailed</MenuItem>
                    <MenuItem value="two-tailed">Two-tailed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Expected attrition"
                  placeholder="e.g. 10%"
                  value={section30.ssAttrition || ""}
                  onChange={(e) => update("ssAttrition", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sample size per group"
                  placeholder="e.g. 8"
                  value={section30.ssSampleSize || ""}
                  onChange={(e) => update("ssSampleSize", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {section30.sampleSizeMethod === "cohens_d" && (
          <Box sx={{ pl: 1.5, borderLeft: "3px solid #e0e0e0" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, fontSize: "0.82rem" }}
            >
              The Cohen&apos;s d method uses a standardised effect size (SES = ES / SD). The SES is
              auto-calculated when you enter both Effect size and Standard deviation.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Effect size (ES)"
                  placeholder="e.g. 1.5"
                  value={section30.ssEffectSize || ""}
                  onChange={(e) => update("ssEffectSize", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Standard deviation (SD)"
                  placeholder="e.g. 2.3"
                  value={section30.ssSd || ""}
                  onChange={(e) => update("ssSd", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>One-tailed or two-tailed test</InputLabel>
                  <Select
                    value={section30.ssTailed || ""}
                    label="One-tailed or two-tailed test"
                    onChange={(e) => update("ssTailed", e.target.value)}
                  >
                    <MenuItem value="one-tailed">One-tailed</MenuItem>
                    <MenuItem value="two-tailed">Two-tailed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="SES = ES / SD"
                  value={autoSES || section30.ssSES || ""}
                  onChange={(e) => !autoSES && update("ssSES", e.target.value)}
                  helperText={
                    autoSES
                      ? "Auto-calculated from the values above."
                      : "Calculated automatically when both ES and SD are filled in."
                  }
                  InputProps={{ readOnly: !!autoSES }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Expected effect size category</InputLabel>
                  <Select
                    value={section30.ssExpectedEffect || ""}
                    label="Expected effect size category"
                    onChange={(e) => update("ssExpectedEffect", e.target.value)}
                  >
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Expected attrition"
                  placeholder="e.g. 10%"
                  value={section30.ssAttrition || ""}
                  onChange={(e) => update("ssAttrition", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sample size per group"
                  placeholder="e.g. 8"
                  value={section30.ssSampleSize || ""}
                  onChange={(e) => update("ssSampleSize", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {section30.sampleSizeMethod === "resource_equation" && (
          <Box sx={{ pl: 1.5, borderLeft: "3px solid #e0e0e0" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, fontSize: "0.82rem" }}
            >
              The resource equation method is used when it is not possible to estimate the standard
              deviation and effect size. The E value is defined as: total number of animals minus
              total number of groups. It should be between 10 and 20.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="E value (total animals − total groups)"
                  placeholder="Must be between 10 and 20"
                  value={section30.ssEValue || ""}
                  onChange={(e) => update("ssEValue", e.target.value)}
                  helperText="E = total number of animals − total number of groups (target: 10–20)."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Expected attrition"
                  placeholder="e.g. 10%"
                  value={section30.ssAttrition || ""}
                  onChange={(e) => update("ssAttrition", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sample size"
                  placeholder="e.g. 8 per group"
                  value={section30.ssSampleSize || ""}
                  onChange={(e) => update("ssSampleSize", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {section30.sampleSizeMethod === "other" && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Describe the method used"
            placeholder="Explain how the sample size was determined, including any references or tools used."
            value={section30.sampleSizeDetails || ""}
            onChange={(e) => update("sampleSizeDetails", e.target.value)}
            sx={{ mt: 0.5 }}
          />
        )}
      </QuestionBlock>

      <Divider sx={{ my: 3 }} />

      {/* ── Q10 ────────────────────────────────────────────────────────── */}
      <QuestionBlock
        number="10"
        label="Which test will you use to analyse the results?"
        description="e.g. t-test, one-way ANOVA, mixed effects model, non-parametric equivalent."
      >
        <TextField
          fullWidth
          size="small"
          label="Statistical test"
          placeholder="e.g. unpaired t-test, one-way ANOVA, Kruskal–Wallis test"
          value={section30.analysisTest || ""}
          onChange={(e) => update("analysisTest", e.target.value)}
        />
      </QuestionBlock>

      {/* ── Generated Summary ──────────────────────────────────────────── */}
      <Box
        sx={{ mt: 4, p: 2.5, borderRadius: 2, backgroundColor: "#fff", border: "1px solid #bdbdbd" }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.75 }}>
          Generated Section 30 Summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This numbered summary is automatically generated from your answers above. It will appear
          in the document preview and export exactly as shown here.
        </Typography>
        {summary.hasContent ? (
          summary.lines.map((line, lineIndex) => (
            <Typography key={lineIndex} variant="body2" sx={{ mb: 0.6, lineHeight: 1.6 }}>
              {line}
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Fill in the questionnaire above to generate the summary.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default Section30Form;
